import type { ReactElement } from 'react';
import { reverse, sortBy } from 'lodash-es';
import type { IMockStore, Ref } from '@graphql-tools/mock';
import type { ApolloCache, NormalizedCacheObject } from '@apollo/client';
import { NotImplementedError } from '../utils/errors';
import { calculateOffset } from '../utils/relay';

export type MockCacheShape = NormalizedCacheObject;

interface Edge<T = Ref> {
    __typename: string;
    node: T;
}

interface RelayResponse<T = Ref> {
    totalCount: number;
    edges: Edge<T>[];
}

export function updateReadyReportsCount(count: number): void {
    const bullet = document.getElementById('download-notification-bullet');

    if (bullet) bullet.dataset.number = String(count);
}

// Helper function for ensuring that a reference from the mock store matches
// a provided query. Checks if all fields in the query match the corresponding
// fields in the reference from the store.
function refMatchesQuery(
    store: IMockStore,
    ref: Ref,
    query: Record<string, unknown>,
    strict: boolean = true
): boolean {
    return Object.entries(query).every(([field, value]) =>
        // eslint-disable-next-line eqeqeq
        strict ? store.get(ref, field) === value : store.get(ref, field) == value
    );
}

export class MockStoreQueryMixin {
    declare store: IMockStore;

    getAll(): Ref[] {
        throw new NotImplementedError();
    }

    // Method to locate a single reference from the mock store entries that
    // matches the provided query. Returns the matching reference.
    get(query: Record<string, unknown>, strict: boolean = true): Ref | undefined {
        return this.getAll().find((ref) =>
            // For each reference(ref) in the mock store entries, checks if all
            // fields in the provided query match the corresponding fields in the
            // reference.
            refMatchesQuery(this.store, ref, query, strict)
        );
    }

    // Method for finding all matching references from the mock store entries
    // based upon a provided query. Returns the matching entries.
    filter(query: object, strict: boolean = true): Ref[] {
        return this.getAll().filter((ref) =>
            // For each reference(ref) in the mock store entries, checks if all
            // fields in the provided query match the corresponding fields in the
            // reference.
            refMatchesQuery(this.store, ref, query as Record<string, unknown>, strict)
        );
    }
}

export class MockStoreHelper<T extends object = object> extends MockStoreQueryMixin {
    cache: ApolloCache<NormalizedCacheObject>;

    typename: string;

    public entries: Ref[];

    constructor(store: IMockStore, cache: ApolloCache<NormalizedCacheObject>, typename: string) {
        super();

        this.store = store; // Instance from `@graphql-tools/mock`.
        this.cache = cache; // Instance of Apollo client's cache.
        this.typename = typename; // GraphQL typename for the helper.

        // Array to retain references added to the mock store. Useful for
        // executing get, filter, or delete operations on the mock store.
        this.entries = [];
    }

    getAll(): Ref[] {
        return this.entries;
    }

    // Method to generate Apollo cache identifier for an object. The
    // `__typename` field is set as the typename given to the helper.
    identify(object: T) {
        const objectWithType = { ...object, __typename: this.typename };
        return this.cache.identify(objectWithType);
    }

    identifyAndGetRef(object: T): Ref {
        // Below extracts 5 out of 'MeasuringPointCommentType:5'.
        const identified = this.identify(object);
        if (!identified) {
            throw new Error(`Failed to identify ${this.typename}`);
        }
        const prefix = `${this.typename}:`;
        if (!identified.startsWith(prefix)) {
            throw new Error(`Unexpected cache id "${identified}" for ${this.typename}`);
        }
        const id = identified.slice(prefix.length);

        return this.getById(id);
    }

    getById(id: string | Ref): Ref {
        return this.store.get(this.typename, id) as Ref;
    }

    // Method for adding a new object to the mock store. Generates a unique id
    // for it and pushes the resultant object to the entries.
    add(object: T): Ref {
        const ref = this.identifyAndGetRef(object);

        // Store the object under the just created reference.
        this.store.set(ref, object);

        // Store the reference in our internal list.
        this.entries.push(ref);

        return ref;
    }

    // Method for deleting a reference from the mock store that matches the
    // provided query.
    delete(query: Record<string, unknown>): Ref | undefined {
        const foundRef = this.get(query);

        if (!foundRef) {
            throw new Error('Ref does not exist.');
        }

        const index = this.entries.findIndex((ref) => ref === foundRef);
        if (index === -1) {
            throw new Error('Tried to remove a query that was not registered');
        }
        this.entries.splice(index, 1);

        return foundRef;
    }

    // Method for updating a reference in the mock store based on identifyArgs,
    // setting new values from the update object.
    update(identifyArgs: T, update: Record<string, unknown>): Ref {
        const ref = this.identifyAndGetRef(identifyArgs);

        Object.entries(update).forEach(([key, value]) => {
            this.store.set(ref, key, value);
        });

        return ref;
    }

    createRelayResponse(
        refs: Ref[],
        orderBy?: string,
        first?: number,
        after?: string
    ): RelayResponse {
        let sortedRefs: Ref[] | null = null;

        if (orderBy) {
            const desc = orderBy.charAt(0) === '-';
            const field = desc ? orderBy.substring(1) : orderBy;

            sortedRefs = sortBy(refs, [(ref: Ref) => this.store.get(ref, field)]);

            if (desc) {
                sortedRefs = reverse(sortedRefs);
            }
        }

        const offset = calculateOffset(after);
        const slicedRefs = (sortedRefs ?? refs).slice(offset, first ? offset + first : undefined);

        return {
            totalCount: refs.length,
            edges: slicedRefs.map((ref) => ({
                __typename: this.typename,
                node: ref,
            })),
        };
    }
}

export function PageBreak(): ReactElement {
    return <div className="pagebreak" />;
}

interface NewPageTableProps {
    title: string;
    header: string[];
    tableData: (string | number | ReactElement)[][];
}

export function NewPageTable({ title, header, tableData }: NewPageTableProps): ReactElement {
    return (
        <>
            <PageBreak />
            <h1>{title}</h1>
            <table className="w-full divide-solid border border-gray-500 text-center align-middle text-xs">
                <thead>
                    <tr>
                        {header.map((name, columnIndex) => (
                            <th key={columnIndex} className="border py-2">
                                {name}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {tableData.map((tableRow, tableRowIndex) => (
                        <tr key={tableRowIndex}>
                            {tableRow.map((content, columnIndex) => (
                                <td key={columnIndex} className="border py-1">
                                    {content}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

export function valueToPercentage(value: number, max: number): number {
    const percentage = (value / max) * 100;

    return percentage > 100 ? 100 : percentage;
}
